import { Portal } from '@radix-ui/react-portal'
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { CSS, styled } from '../../lib/stitches'
import { gray4 } from '../platform/color'
import { Row, StyledScrollable } from './Components'
import { CheckmarkIcon, ChevronDown } from './icons'

const DropdownLabel = styled('span', {
  fontSize: '16px',
  lineHeight: '20px',
  fontWeight: 500,
  maringRight: '8px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  variants: {
    slim: {
      true: {
        fontSize: '14px',
        marginRight: '6px'
      }
    }
  }
})

const DropdownItem = styled('div', {
  padding: '8px 16px',
  fontSize: '14px',
  lineHeight: '18px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  '&:hover': {
    background: 'rgba(0, 0, 0, 0.1)'
  }
})

const DropdownContainer = styled(StyledScrollable, {
  zIndex: '2000',
  position: 'fixed',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  padding: '8px 0px',
  background: 'var(--studio-very-light)',
  borderRadius: '4px',
  border: `1px solid ${gray4}`,
  boxShadow: '0px 8px 16px -4px #2727271a',
  overflowY: 'scroll',
  maxHeight: '400px',
  pointerEvents: 'all'
})

export const DropdownWrapper = styled('div', {
  cursor: 'pointer',
  padding: '8px 14px',
  position: 'relative',
  borderRadius: '5px',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  flex: '1',
  width: '100%',
  variants: {
    slim: {
      true: {
        padding: '0px 3px'
      }
    }
  }
})

export interface DropdownOption<T = any> {
  value: T
  label: string
}

interface DropdownProps<T> {
  value?: T
  options: DropdownOption<T>[]
  placeholder?: string
  style?: React.CSSProperties
  onSelect?: (option: DropdownOption<T>) => void
  slim?: boolean
  disabled?: boolean
  labelCSS?: CSS
}

interface DropdownListItemProps {
  onClick?: React.MouseEventHandler<HTMLDivElement>
  active?: boolean
}

export const DropdownListItem: React.FC<PropsWithChildren<DropdownListItemProps>> = ({
  onClick,
  children,
  active
}) => (
  <DropdownItem onClick={onClick}>
    {children}
    <CheckmarkIcon
      height="16px"
      width="16px"
      style={{ marginLeft: '10px', color: active ? 'currentcolor' : 'transparent' }}
    />
  </DropdownItem>
)

const Dropdown: React.FC<DropdownProps<any>> = ({
  value,
  style,
  placeholder,
  options,
  onSelect,
  slim,
  disabled,
  labelCSS
}) => {
  const trigger = useRef<HTMLDivElement>()
  const dropdownContainer = useRef<HTMLDivElement>()
  const [isOpen, setIsOpen] = useState(false)

  const boundingRect = trigger.current && trigger.current.getBoundingClientRect()

  useEffect(() => {
    if (!dropdownContainer.current || !isOpen) return

    const handleDocumentClick = (evt: MouseEvent) => {
      const dropdownBoundingRect = dropdownContainer.current.getBoundingClientRect()
      if (
        !(
          evt.pageX <= dropdownBoundingRect.right &&
          evt.pageX >= dropdownBoundingRect.left &&
          evt.pageY <= dropdownBoundingRect.bottom &&
          evt.pageY >= dropdownBoundingRect.top
        )
      )
        handleClose()
    }

    document.addEventListener('click', handleDocumentClick)

    return () => {
      document.removeEventListener('click', handleDocumentClick)
    }
  }, [isOpen, dropdownContainer.current])

  function handleClose() {
    setIsOpen(false)
    window.removeEventListener('click', this)
  }

  const handleSelect = (
    evt: React.MouseEvent<HTMLDivElement, MouseEvent>,
    { value, label }: DropdownOption
  ) => {
    evt.stopPropagation()
    onSelect && onSelect({ value, label })
    handleClose()
  }

  const handleClick = (evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (disabled) return
    if (isOpen) return handleClose()
    evt.stopPropagation()

    setIsOpen(true)
    window.addEventListener('click', handleClose)
  }

  const dropdownCSS = { left: boundingRect && boundingRect.left, top: undefined, bottom: undefined }
  if (boundingRect && dropdownContainer.current) {
    if (boundingRect.bottom + dropdownContainer.current.offsetHeight < window.innerHeight)
      dropdownCSS.top = boundingRect.bottom
    else dropdownCSS.bottom = 0
  }

  return (
    <DropdownWrapper style={style} slim={slim}>
      <Row
        onClick={handleClick}
        ref={trigger}
        css={{ width: '100%' }}
        verticalCenter
        justifySpaceBetween
      >
        <Row verticalCenter>
          <DropdownLabel slim={slim} css={labelCSS}>
            {options.find((element) => element.value === value)?.label ?? placeholder ?? 'No value'}
          </DropdownLabel>
        </Row>
        <Row verticalCenter justifyContentEnd>
          <ChevronDown height={slim ? '12px' : '16px'} width={slim ? '12px' : '16px'} />
        </Row>
      </Row>

      <Portal container={document.body}>
        <DropdownContainer
          css={{ visibility: isOpen ? 'visible' : 'hidden', ...dropdownCSS }}
          ref={dropdownContainer}
        >
          {options.map((entry) => (
            <DropdownListItem
              key={entry.label}
              onClick={(evt) => handleSelect(evt, entry)}
              active={entry.value === value}
            >
              {entry.label}
            </DropdownListItem>
          ))}
        </DropdownContainer>
      </Portal>
    </DropdownWrapper>
  )
}

export default Dropdown
